import { Injectable, OnDestroy, inject } from '@angular/core';

import { Subscription, interval } from 'rxjs';
import { map } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { TokenActions } from '../app/store/token.actions';
import { tokenFeature } from '../app/store/token.feature';

interface TokenValidationResult {
  isValid: boolean;
  timeLeft: number | null;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TokenExpirationService implements OnDestroy {
  private readonly store = inject(Store);
  private readonly CHECK_INTERVAL = 60000; // Check every minute
  private readonly EXPIRATION_THRESHOLD = 10 * 60; // 10 minutes in seconds
  private subscription: Subscription | null = null;

  constructor() {
    // Log initialization, but don't start checks here
    console.info('[TokenExpirationService] Initialized.');
  }

  ngOnDestroy(): void {
    console.info('[TokenExpirationService] Stopping token expiration monitoring service (OnDestroy)');
    this.stopMonitoring();
  }

  public startMonitoring(): void {
    console.info('[TokenExpirationService] Starting token expiration monitoring.');
    // Prevent starting multiple intervals if called again
    if (this.subscription) {
      console.warn('[TokenExpirationService] Monitoring already active. Skipping start.');
      return;
    }

    // Perform immediate check when monitoring starts
    console.info('[TokenExpirationService] Performing initial token validation on startMonitoring');
    const validationResult = this.validateToken('initial'); // Changed context label

    if (!validationResult.isValid) {
      console.warn('[TokenExpirationService] Token validation failed on startMonitoring:', validationResult.error);
      // Don't stop monitoring here, let the caller (effects) handle logout/stop
      // Dispatching logout here might cause issues if called during effects processing
      // this.store.dispatch(TokenActions.logout());
      return; // Don't start the interval if invalid immediately
    }

    // Start periodic checks only if initial validation passed
    this.startExpirationCheck();
  }

  public stopMonitoring(): void {
    console.info('[TokenExpirationService] Explicitly stopping token expiration monitoring.');
    this.stopExpirationCheck();
  }

  private validateToken(context: 'initial' | 'periodic' | 'manual'): TokenValidationResult {
    const tokenString = this.store.selectSignal(tokenFeature.selectTokenString)();

    if (!tokenString) {
      const message = `[TokenExpirationService] No token found during ${context} check`;
      console.warn(message);
      return { isValid: false, timeLeft: null, error: message };
    }

    try {
      const tokenPayload = JSON.parse(atob(tokenString.split('.')[1]));
      const expTime = typeof tokenPayload.exp === 'number' ? tokenPayload.exp : null;

      if (expTime === null) {
        const message = `[TokenExpirationService] Token does not contain expiration time`;
        console.warn(message);
        return { isValid: false, timeLeft: null, error: message };
      }

      const now = Math.floor(Date.now() / 1000);
      const timeLeft = expTime - now;

      console.debug(`[TokenExpirationService] ${context} check - Token expires in`, timeLeft, 'seconds');

      if (timeLeft <= 0) {
        return { isValid: false, timeLeft, error: 'Token has expired' };
      }

      if (timeLeft <= this.EXPIRATION_THRESHOLD) {
        return { isValid: false, timeLeft, error: 'Token will expire soon' };
      }

      return { isValid: true, timeLeft };
    } catch (error) {
      const message = `[TokenExpirationService] Failed to parse JWT token: ${error}`;
      console.error(message);
      return { isValid: false, timeLeft: null, error: message };
    }
  }

  private startExpirationCheck(): void {
    console.debug('[TokenExpirationService] Setting up periodic token checks every', this.CHECK_INTERVAL / 1000, 'seconds');

    this.subscription = interval(this.CHECK_INTERVAL)
      .pipe(map(() => this.validateToken('periodic')))
      .subscribe((result) => {
        if (!result.isValid) {
          console.warn('[TokenExpirationService] Periodic token validation failed:', result.error);
          this.stopExpirationCheck(); // Stop checking
          this.store.dispatch(TokenActions.logout()); // Trigger logout process
        } else {
          console.debug('[TokenExpirationService] Token is still valid for', result.timeLeft, 'seconds');
        }
      });
  }

  private stopExpirationCheck(): void {
    if (this.subscription) {
      console.debug('[TokenExpirationService] Cleaning up interval subscription');
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }
}
